/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from "react";
import { AsgardeoSPAClient } from "@asgardeo/auth-react";

import { makeStyles } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import Button from "@material-ui/core/Button";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Avatar from "@material-ui/core/Avatar";
import ListItemText from "@material-ui/core/ListItemText";
import HotjarMask from "../../../util/HotJarMask";

const useStyles = makeStyles((theme) => ({
  paragraph: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    textTransform: "capitalize",
  },
  popover: {
    marginTop: theme.spacing(2),
  },
  button: {
    color: "inherit",
    borderColor: "currentColor",
  },
  listItem: {
    paddingTop: "10px",
    paddingLeft: "32px",
    paddingRight: "32px",
    paddingBottom: "10px",
  },
}));

interface UserMenuProps {
  userFullName: string;
  userEmail: string;
  profileImage: string;
}

function UserMenu(props: UserMenuProps) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const authClient = AsgardeoSPAClient.getInstance();
  const { userEmail, userFullName, profileImage } = props;

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    authClient?.signOut();
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <HotjarMask isMasked>
        <Button
          disableRipple
          aria-describedby={id}
          className={classes.button}
          onClick={handleClick}
          data-testid={"signedin-user-menu-btn"}
        >
          <p className={classes.paragraph}>{userFullName}</p>
          <Avatar alt={userFullName} src={profileImage} />
        </Button>
      </HotjarMask>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        className={classes.popover}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <List>
            <HotjarMask isMasked>
              <ListItem className={classes.listItem}>
                <ListItemAvatar>
                  <Avatar alt={userFullName} src={profileImage} />
                </ListItemAvatar>
                <ListItemText primary={userFullName} secondary={userEmail} />
              </ListItem>
            </HotjarMask>
            <ListItem
              button
              onClick={handleSignOut}
              className={classes.listItem}
              data-testid={"logout-item-btn"}
            >
              <ListItemIcon>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </ClickAwayListener>
      </Popover>
    </div>
  );
}

export default UserMenu;
